import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, interval, map, Observable, startWith, Subject, switchMap, takeUntil } from "rxjs";
import { UserUsage } from "../interfaces/user-usage";
import { DEFAULT_MAX_USER_USAGE, NOTEBOOK_CPU_RELEVANT_STATUSES, NOTEBOOK_RUNNING_RELEVANT_STATUSES, NOTEBOOK_STOPPED_RELEVANT_STATUSES, NOTEBOOK_STORAGE_RELEVANT_STATUSES } from "../config/notebook-config";
import { NotebookService } from "./notebook.service";
import { NotebookDisplayed } from "../interfaces/notebook-displayed";
import { NOTEBOOK_TRANSITIONAL_STATUSES,   NOTEBOOK_STATUS_LABELS, } from "../config/notebook-config";

@Injectable({ providedIn: 'root' })
export class ConsumptionService {
  readonly transitionalStatuses = NOTEBOOK_TRANSITIONAL_STATUSES;
  readonly notebookStatusLabels= NOTEBOOK_STATUS_LABELS;
  readonly notebookRunningRelevantSatuses=NOTEBOOK_RUNNING_RELEVANT_STATUSES;
  readonly notebookStoppedRelevantStatuses=NOTEBOOK_STOPPED_RELEVANT_STATUSES;
  readonly notebookCpuRelevantStatuses=NOTEBOOK_CPU_RELEVANT_STATUSES;
  readonly notebookStorageRelevantStatuses=NOTEBOOK_STORAGE_RELEVANT_STATUSES;
  private maxAllowedUsage: UserUsage = structuredClone(DEFAULT_MAX_USER_USAGE);
  
  private currentUsage$ = new BehaviorSubject<UserUsage>({
    cpu: 0,
    ram: 0,
    storage: 0,
    notebooks: { running: 0, stopped: 0 }
  });
  private destroy$ = new Subject<void>();
  private notebooks$ = new BehaviorSubject<NotebookDisplayed[]>([]);

  // check to avoid multiple loads
  private initialized = false;

  private pollingIntervalFast = 5000;
  private pollingIntervalSlow = 20000;
  constructor() {}

  //*******************  init and state handling ************************ 
  // initialize once - to call from components 
  initUsage(notebookService: NotebookService): void {
    if (!this.initialized) {
      this.initialized = true;
      this.loadAndCalculateUsage(notebookService); // quota check
    }
  }

  // current usage data 
   loadAndCalculateUsage(notebookService: NotebookService): void {
    notebookService.getAllNotebooks().subscribe((notebooks) => {
      
      // calculate running and stopped 
      const running = notebooks.filter(nb => {
        const status = nb.status.toLowerCase();
        return this.notebookRunningRelevantSatuses.includes(status);
      }); 
      
      const stopped = notebooks.filter(nb => {
        const status = nb.status.toLowerCase();
        return this.notebookStoppedRelevantStatuses.includes(status);
      });
      

      //exclude stopped and stopping (in process) and failed nb for cpu calculation:
     const cpuRelevant = notebooks.filter(nb => (this.notebookCpuRelevantStatuses.includes(nb.status.toLowerCase())) );
      const sumCpu = cpuRelevant.reduce((sum, nb) => sum + nb.cpu, 0);
  
      //ram only for running 
      const sumRam = running.reduce((sum, nb) => sum + nb.ram, 0);

     //exclude stopped and stopping (in process) and failed nb for storage calculation:
    const storageRelevant = notebooks.filter(nb =>(this.notebookStorageRelevantStatuses.includes(nb.status.toLowerCase())) );
    const sumStorage = storageRelevant.reduce((sum, nb) => sum + nb.storage, 0);

      const updatedUsage: UserUsage = {
        cpu: sumCpu,
        ram: Math.floor(sumRam ),
        storage: Math.floor(sumStorage ),
        notebooks: {
          running: running.length,
          stopped: stopped.length
        }
      };
      this.setCurrentUsage(updatedUsage);
      //console.log("updatedUsage: ",updatedUsage);
      this.notebooks$.next(notebooks);
    });
  }
  


    private setCurrentUsage(usage: UserUsage): void {
    this.currentUsage$.next(usage);
  }

  getMaxAllowedUsage(): UserUsage {
    return this.maxAllowedUsage;
  }
  //*******************  observables and data streams ************************

    getCurrentUsage$() {
    return this.currentUsage$.asObservable();
  }

  getNotebooks$() {
    return this.notebooks$.asObservable();
  }


  //display quota usage for charts 
 getQuotaUsed(
  usage: UserUsage,
  notebookStatuses: string[]
): {
  cpu: { label: string; width: string },
  ram: { label: string; width: string },
  storage: { label: string; width: string }
} {
const hasUpdating = notebookStatuses.some(
  status => this.transitionalStatuses.includes(status.toLowerCase())
);


  const safePercent = (current: number, max: number): string =>
    max > 0 ? Math.round((current / max) * 100) + '%' : '0%';

  const getValue = (current: number, max?: number) => {
    if (hasUpdating) return { label: 'Updating...', width: '0%' };
    if (max === undefined) return { label: `${current} GB`, width: '100%' }; // storage has no max
    return { label: safePercent(current, max), width: safePercent(current, max) };
  };

return {
  cpu: getValue(usage.cpu, this.maxAllowedUsage.cpu),
  ram: getValue(usage.ram ?? 0, this.maxAllowedUsage.ram),
  storage: getValue(usage.storage ?? 0, this.maxAllowedUsage.storage)
};
}


  getQuotaUsed$(): Observable<{
    cpu: { label: string; width: string },
    ram: { label: string; width: string },
    storage: { label: string; width: string }
  }> {
    return combineLatest([this.getCurrentUsage$(), this.getNotebooks$()]).pipe(
      map(([usage, notebooks]) => {
        const statuses = notebooks.map(nb => nb.status ?? '');
        return this.getQuotaUsed(usage, statuses);
      })
    );
  }

    //*******************  quota validation and monitoring************************
  
  isCpuExceeded(currentNotebookCpu: number): Observable<boolean> {
    return this.currentUsage$.pipe(
      map(usage => {
        const currentCpu = usage.cpu ?? 0;
        const maxCpu = this.maxAllowedUsage.cpu;
        const total = currentCpu + currentNotebookCpu;
        const exceeded = total > maxCpu;
  
     //console.log(`[CPU Check] Current: ${currentCpu}, Input: ${currentNotebookCpu}, Total: ${total}, Max Allowed: ${maxCpu}, Exceeded: ${exceeded}`);
  
        return exceeded;
      })
    );
  }

  isStoppedLimitExceeded(): Observable<boolean> {
  return this.currentUsage$.pipe(
    map(usage => {
      const currentStopped = usage.notebooks.stopped ?? 0;
      const maxStopped = this.maxAllowedUsage.notebooks.stopped;
      const wouldExceed = (currentStopped + 1) > maxStopped;

     // console.log(`[Stopped Check] Current: ${currentStopped}, +1 => ${currentStopped + 1}, Max Allowed: ${maxStopped}, Exceeded: ${wouldExceed}`);

      return wouldExceed;
    })
  );
}


  initQuotaMonitoring(notebookService: NotebookService): void {
   this.loadAndCalculateUsage(notebookService);  //quota check
  
    const pollNotebooks = (intervalTime: number): Observable<NotebookDisplayed[]> => {
      return interval(intervalTime).pipe(
        startWith(0),
        switchMap(() => notebookService.getAllNotebooks())
      );
    };
  
    let isSlowPolling = false;
  
    const subscription = pollNotebooks(this.pollingIntervalFast)
      .pipe(takeUntil(this.destroy$))
      .subscribe(notebooks => {
        const statuses = notebooks.map(nb => nb.status?.toLowerCase() ?? '');
        const allDefinitive = statuses.every(
          status => status === this.notebookStatusLabels['running'] || status === this.notebookStatusLabels['stopped']
        );
  
     this.loadAndCalculateUsage(notebookService);  //quota check
  
        // if all definitive state and not already slow polling switch to it
        if (allDefinitive && !isSlowPolling) {
         // console.log('All notebooks stable — switching to slow polling');
          subscription.unsubscribe();
          isSlowPolling = true;
  
          pollNotebooks(this.pollingIntervalSlow)
            .pipe(takeUntil(this.destroy$))
            .subscribe(slowNotebooks => {
             this.loadAndCalculateUsage(notebookService);  //quota check
            });
        }
      });
  }
  
  cleanupQuotaMonitoring(): void {
    this.destroy$ = new Subject<void>(); 
  }
}
